import React, { useState } from "react";
import {
  Box,
  Button,
  CardMedia,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import ReactPlayer from "react-player";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
};

/**
 * Lightweight scrubber to pick a cover frame TIME (not an image).
 * Scrub / pause the video at the desired frame, then confirm — returns the
 * paused time as integer seconds via onSave(seconds).
 */
export default function CoverFramePicker({ open, onClose, url, onSave }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [currentTime, setCurrentTime] = useState(0);
  const [pausedAt, setPausedAt] = useState(null);

  const handleClose = () => {
    setPausedAt(null);
    setCurrentTime(0);
    onClose();
  };

  const handleConfirm = () => {
    onSave(Math.round(pausedAt ?? currentTime));
    handleClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      fullScreen={fullScreen}
      aria-labelledby="cover-frame-title"
      PaperProps={{ sx: { borderRadius: fullScreen ? 0 : 2, overflow: "hidden" } }}
    >
      <DialogTitle id="cover-frame-title" component="div" sx={{ p: 0 }}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.5}
          sx={{
            px: { xs: 2, sm: 3 },
            py: 2,
            bgcolor: "grey.900",
            color: "common.white",
          }}
        >
          <AccessTimeIcon />
          <Box minWidth={0}>
            <Typography variant="h6" fontWeight={600} component="h2">
              Pick Cover Frame Time
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.75 }}>
              Pause the video on the frame TikTok should use as the cover
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: "background.default" }}>
        <CardMedia
          component={ReactPlayer}
          url={url}
          controls
          width="100%"
          height="auto"
          onProgress={({ playedSeconds }) => setCurrentTime(playedSeconds)}
          onPause={() => setPausedAt(currentTime)}
          onPlay={() => setPausedAt(null)}
          sx={{
            maxHeight: { xs: "50vh", sm: "360px" },
            minHeight: 200,
            borderRadius: 2,
            bgcolor: "grey.900",
            overflow: "hidden",
            "& video": { maxHeight: "360px !important", borderRadius: 8 },
          }}
        />
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={1}
          mt={1.5}
        >
          <Chip
            size="small"
            icon={<AccessTimeIcon sx={{ fontSize: "14px !important" }} />}
            label={pausedAt !== null ? `Paused @ ${formatTime(pausedAt)}` : `Playing… ${formatTime(currentTime)}`}
            aria-live="polite"
            sx={{ fontWeight: 600, bgcolor: alpha(theme.palette.text.primary, 0.08) }}
          />
          <Typography variant="caption" color="text.secondary">
            Scrub to the moment you want, then pause.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions
        sx={{
          px: { xs: 2, sm: 3 },
          py: 2,
          bgcolor: "background.paper",
          borderTop: "1px solid",
          borderColor: "divider",
          flexDirection: { xs: "column-reverse", sm: "row" },
          gap: 1,
          "& > :not(style) + :not(style)": { ml: { xs: 0, sm: 1 } },
        }}
      >
        <Button
          onClick={handleClose}
          variant="outlined"
          color="inherit"
          sx={{ textTransform: "none", fontWeight: 600, minHeight: { xs: 44, sm: 36 }, width: { xs: "100%", sm: "auto" } }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={pausedAt === null}
          startIcon={<CheckCircleIcon />}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            minHeight: { xs: 44, sm: 36 },
            width: { xs: "100%", sm: "auto" },
            bgcolor: "grey.900",
            color: "common.white",
            "&:hover": { bgcolor: "grey.800" },
            "&:focus-visible": { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
          }}
        >
          Use This Frame ({formatTime(pausedAt ?? currentTime)})
        </Button>
      </DialogActions>
    </Dialog>
  );
}
